import React, { useMemo, useState, useCallback, useEffect, useContext, useRef } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ENTER_NAME } from '../ISR/ISRConstants';
import { type StylesConfig } from "react-select";
import debounce from 'lodash/debounce';
import { AppContext } from 'Context/AppContext';
import { getUserList } from './services/RuleService';
import Select from "react-select";

export type User = {
    first_name: string | null;
    last_name: string | null;
    id: string;
    industry_id: string;
    is_deleted: boolean;
    party_id: string;
}

type ChangeOwnerDialogProps = {
    id: string;
    dialogHeader: string;
    labelName: string;
    subtitle?: string;
    value?: User;
    showCancelButton?: boolean;
    actionLabel?: string;
    onClose: () => void;
    onSubmit: (user: User) => void;
    showIndicator?: boolean;
}

/**
 * The Change Owner react-select skin, on role tokens.
 *
 * react-select paints from JS, so no `[data-theme]` could reach this control: the menu
 * was `white` with `#172B4D` rows and a `#0747a6` selection, baked into a style object.
 * Reported against the dark theme on 2026-09-22 — the dialog around it themed correctly
 * and the dropdown inside it stayed a white slab, which is what made it obvious.
 *
 * **This is the blind spot `rulesPaletteWall` cannot see** (design-system § 322): a wall
 * reads `tw-` classes out of the file, and a component that colours itself in a style
 * object has no classes to read. The wall reported this module at zero while this control
 * was light-only. If you add a react-select anywhere, its skin is your job, not the
 * wall's.
 *
 * `var(--ds-*)` resolves in an inline style exactly as it does in a stylesheet, so these
 * follow the theme with no JS listener. Every fallback is the LIGHT value of its role, so
 * a stylesheet that failed to load still renders a usable control rather than a
 * transparent one.
 *
 * **Why these roles and not the Recategorization skin's**
 * (`Components/GMS/Products/Recategorization/utils/Styles.tsx`, the older copy): this
 * select lives inside a DS `Dialog`, whose panel is `surface-raised`. A menu on `surface`
 * would sit BELOW its own container in dark (#141c2c against #1a2438) and read as a hole
 * rather than a floating list — § 256's point about hover grounds, applied to a popover.
 * So the menu takes `surface-raised` + an `edge` border, and the rows take
 * `accent-tint` / `surface-hover-strong`: the DS `Dropdown` idiom, which is what this
 * control is surrounded by and what it becomes when § 210 lands `Combobox`.
 */
const ROLE = {
    edge: 'var(--ds-edge, #dadfe8)',
    accent: 'var(--ds-accent, #030562)',
    accentTint: 'var(--ds-accent-tint, #bcddff)',
    surface: 'var(--ds-surface, #ffffff)',
    surfaceRaised: 'var(--ds-surface-raised, #ffffff)',
    surfaceHoverStrong: 'var(--ds-surface-hover-strong, #f2f4f8)',
    ink: 'var(--ds-ink, #001233)',
    inkSecondary: 'var(--ds-ink-secondary, #33415c)',
    inkTertiary: 'var(--ds-ink-tertiary, #5c677d)',
} as const;

export const customStyles: StylesConfig<User, false> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: ROLE.surface,
        border: `1px solid ${state.isFocused ? ROLE.accent : ROLE.edge}`,
        boxShadow: state.isFocused ? `0 0 0 1px ${ROLE.accent}` : 'none',
        '&:hover': {
            border: `1px solid ${state.isFocused ? ROLE.accent : ROLE.edge}`
        }
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? ROLE.accentTint
            : state.isFocused
                ? ROLE.surfaceHoverStrong
                : 'transparent',
        color: state.isSelected ? ROLE.accent : ROLE.ink,
        cursor: 'pointer',
        '&:active': {
            backgroundColor: state.isSelected ? ROLE.accentTint : ROLE.surfaceHoverStrong
        }
    }),
    /* The closed control's own text, and the text being typed into it. Both
       inherited `color` from react-select's defaults, which is a fixed near-black. */
    singleValue: (provided) => ({ ...provided, color: ROLE.ink }),
    input: (provided) => ({ ...provided, color: ROLE.ink }),
    placeholder: (provided) => ({ ...provided, color: ROLE.inkSecondary }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        color: state.isFocused ? ROLE.ink : ROLE.inkTertiary,
        '&:hover': { color: ROLE.ink }
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: ROLE.surfaceRaised,
        border: `1px solid ${ROLE.edge}`,
        zIndex: 9999
    }),
    /* react-select paints the scrolling list its own ground; left transparent so the
       menu's single surface shows through and the rows are the only painted band. */
    menuList: (provided) => ({ ...provided, backgroundColor: 'transparent' })
};

const ChangeOwnerDialog = ({
    id,
    dialogHeader = "",
    labelName = "",
    subtitle,
    value,
    onClose,
    onSubmit,
    showCancelButton = true,
    actionLabel,
    showIndicator = false,
}: ChangeOwnerDialogProps) => {
    const [user, setUser] = useState<User | null>(value ?? null);
    const [loading, setLoading] = useState(false);
    const [hasMoreSearch, setHasMoreSearch] = useState(true);
    const [hasMoreNormal, setHasMoreNormal] = useState(true);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [normalPage, setNormalPage] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));
    const [isSearching, setIsSearching] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const isMounted = useRef(true);
    const limit = 10;

    const appContext = useContext(AppContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.sams_id;
    const partyId = appContext?.get_selected_party()?.sams_id;

    const fetchUsers = useCallback(async (pageNum: number, search: string) => {
        if (!partyId || !industryId || !isMounted.current) return;

        setLoading(true);
        try {
            const response = await getUserList(
                partyId,
                industryId,
                (pageNum - 1) * limit,
                limit,
                search
            );

            const totalCount = parseInt(response?.data?.data?.total_count || '0');
            const newUsers = response?.data?.data?.users || [];
            const currentCount = (pageNum - 1) * limit + newUsers.length;

            if (search) {
                setFilteredUsers(prev =>
                    pageNum === 1 ? newUsers : [...prev, ...newUsers]
                );
                setSearchPage(pageNum);
                setHasMoreSearch(currentCount < totalCount);
            } else {
                setAllUsers(prev => {
                    const uniqueValues = [...prev];
                    newUsers.forEach((newVal: User) => {
                        if (!uniqueValues.some(existing => existing.id === newVal.id)) {
                            uniqueValues.push(newVal);
                        }
                    });
                    return uniqueValues;
                });
                loadedPagesRef.current.add(pageNum);
                setNormalPage(prevPage => Math.max(prevPage, pageNum));
                setHasMoreNormal(currentCount < totalCount);
            }
        } catch (error) {
            console.error("Error fetching users", error);
        } finally {
            setLoading(false);
        }
    }, [industryId, partyId, limit]);

    const selectProps = useMemo(() => ({
        id: "usersId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: ROLE.accent
            }),
            /* The DS Dialog body is `tw-overflow-y-auto`, which CLIPS an
               absolutely-positioned menu at the footer band — the menu must
               portal out of the dialog to be visible. Same fix as the other
               react-selects that live inside clipping containers
               (Mapping's AttributeContent, ComputedSubstitutions' FilterModal):
               portal to <body> and lift above the overlay tier. */
            menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
        },
        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: ENTER_NAME,
        isLoading: loading,
        getOptionLabel: (option: User) => {
            const firstName = option?.first_name || '';
            const lastName = option?.last_name || '';
            return `${firstName} ${lastName}`.trim() || 'Unnamed User';
        }
    }), [loading]);
    useEffect(() => {
        if (!isSearching && allUsers.length === 0) {
            fetchUsers(1, "");
        }
    }, [fetchUsers, isSearching]);
    const handleMenuScrollToBottom = useCallback(() => {
        if (isSearching && hasMoreSearch) {
            const nextSearchPage = searchPage + 1
            fetchUsers(nextSearchPage, searchValue)
        } else if (!isSearching && hasMoreNormal) {
            const nextNormalPage = normalPage + 1
            if (!loadedPagesRef.current.has(nextNormalPage)) {
                fetchUsers(nextNormalPage, "")
            }
        }
    }, [
        isSearching,
        hasMoreSearch,
        hasMoreNormal,
        searchPage,
        normalPage,
        searchValue,
        fetchUsers
    ])

    const handleConfirmClick = () => {
        if (user == null) return;
        onSubmit(user);
    };

    /*
        § 209 — while the parent's transfer is in flight (`showIndicator`; only
        ListView passes it, as `changeOwnerInProgress`) the dialog is COMMITTED:
        the call is not abortable, and a mid-flight close would only hide the
        dialog while the toast + refetch still land. One guard covers every
        close route (Cancel, the X, Escape — Dialog routes all three through
        `onClose`); the parent's `finally` always drops the flag, so this can
        never wedge shut. The ISRProjectTable and RuleTable mounts never pass
        the flag and are byte-identical.
    */
    const guardedClose = () => {
        if (showIndicator) return;
        onClose();
    };

    const handleFieldChange = (newValue: User | null) => {
        setUser(newValue ?? null);
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((input: string) => {
                setIsSearching(true);
                setSearchPage(1);
                setHasMoreSearch(true);
                setFilteredUsers([]);
                fetchUsers(1, input);
            }, 300),
        [fetchUsers]
    );

    const debouncedLoadMoreOptions = useCallback(
        debounce(() => {
            const nextPage = isSearching ? searchPage + 1 : normalPage + 1;

            if (isSearching && hasMoreSearch) {
                fetchUsers(nextPage, searchValue);
            } else if (!isSearching && hasMoreNormal && !loadedPagesRef.current.has(nextPage)) {
                fetchUsers(nextPage, "");
            }
        }, 300),
        [hasMoreNormal, hasMoreSearch, normalPage, searchPage, isSearching, searchValue]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            debouncedSearch.cancel();
            debouncedLoadMoreOptions.cancel();
            setSearchValue("");
            setNormalPage(1);
            setSearchPage(1);
            setHasMoreNormal(true);
            setHasMoreSearch(true);
        };
    }, []);



    const resetSearchState = useCallback(() => {
        setFilteredUsers([])
        setSearchPage(1)
        setHasMoreSearch(true)
        setIsSearching(false)
        setSearchValue("")
    }, [])

    const handleInputChange = useCallback(
        (input: string, { action }: { action: string }) => {
            if (action === "input-blur" || action === "menu-close" || action === "set-value") return;

            if (input === "") {
                resetSearchState();
                fetchUsers(1, "");
                return;
            }

            setSearchValue(input);
            debouncedSearch(input);
        },
        [fetchUsers, resetSearchState, debouncedSearch]
    );

    const handleMenuClose = useCallback(() => {
        if (!searchValue) return

        resetSearchState()
        if (allUsers.length === 0) {
            fetchUsers(normalPage, "")
        }
    }, [searchValue, normalPage, resetSearchState, allUsers?.length])

    useEffect(() => {
        return () => {
            isMounted.current = false
            resetSearchState()
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
        }
    }, [resetSearchState])

    /*
     * The dialog shell is the DS Dialog (§ 97); the react-select user picker is
     * DELIBERATELY kept below. TODO(design-system): it is a server-paginated
     * infinite-scroll async search (onMenuScrollToBottom fetches the next page,
     * onInputChange debounces remote queries) — functionality the DS Dropdown's
     * client-side filter does not have. Replace only when the DS grows an async
     * combobox.
     */
    return (
        <Dialog
            isOpen
            onClose={guardedClose}
            title={dialogHeader}
            data-testid={id}
            footer={
                <div id={`${id}-actions`} className="tw-flex tw-w-full tw-justify-between tw-items-center">
                    <div className="tw-min-w-0">
                        {showIndicator ? (
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Loading...</Typography>
                        ) : null}
                    </div>
                    <div className="tw-flex tw-gap-2 tw-justify-end">
                        {showCancelButton ? (
                            <Button id={`${id}-cancel-btn`} data-testid={`${id}-cancel-btn`} variant="secondary" disabled={showIndicator} onClick={guardedClose}>
                                Cancel
                            </Button>
                        ) : null}
                        <Button
                            id={`${id}-confirm-btn`}
                            data-testid={`${id}-confirm-btn`}
                            variant="primary"
                            disabled={user == null}
                            loading={showIndicator}
                            onClick={handleConfirmClick}
                        >
                            {actionLabel ?? 'Confirm'}
                        </Button>
                    </div>
                </div>
            }
        >
                <div>
                    {subtitle && (
                        <p className="tw-text-sm tw-text-ink-secondary tw-mb-3">{subtitle}</p>
                    )}
                    <label className="tw-font-normal tw-w-full tw-text-sm tw-ps-1">{labelName}</label>
                    <div className="tw-relative tw-mt-2">
                        <Select<User>
                            {...selectProps}
                            options={isSearching ? filteredUsers : allUsers}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            onInputChange={handleInputChange}
                            onChange={(selectedOption) => {
                                if (selectedOption) {
                                    handleFieldChange(selectedOption)
                                }
                            }}
                            onMenuClose={handleMenuClose}
                            value={user}
                            isMulti={false}
                            getOptionValue={(option: User) => option.id}
                            components={{
                                IndicatorSeparator: () => null,
                                NoOptionsMessage: () => (
                                    <div className="tw-p-2 tw-text-center tw-text-ink-secondary">
                                        No users available
                                    </div>
                                )
                            }}
                        />
                    </div>
                </div>
        </Dialog>
    );
};

export default ChangeOwnerDialog;